/* eslint-disable max-lines */

import { GET_CLOUD_PROXY_ENVIRONMENT_REQUESTS } from 'components/cloud/constants/ApiUri';
import {
  EnvironmentRequest,
  EnvironmentState,
  GroupMetadata,
  PatchMetadata,
  RequestId
} from 'vrlcm-gui-types';
import { Http } from 'vrlcm-gui-utils';
import { UPDATE_VIDM_NODE_ADMIN_PASSWORD_URL } from '../../locker/constants/ApiUri';
import { CompatibleProduct } from '../components/environments/actions/compatibility-matrix/type';
import { DeleteOptions } from '../components/environments/actions/delete-environment/DeleteEnvironment';
import { Snapshot } from '../components/environments/actions/manage-snapshots/snapshots-explorer/types';
import {
  CONFIGURE_VROPS_AS_WLP_URL,
  DELETE_ENVIRONMENT_URL,
  DELETE_PRODUCT_URL,
  DOWNLOAD_ENVIRONMENT_LOGS_URL,
  DOWNLOAD_PRODUCT_LOGS_URL,
  EXPORT_CONFIGURATION_URL,
  EXPORT_PRODUCT_CONFIGURATION_URL,
  GET_ALL_ENVIRONMENT_URL,
  GET_AVAILABLE_PRODUCT_PATCHES_URL,
  GET_COMPATIBILITY_MATRIX,
  GET_ENVIRONMENT_ACTIVE_REQUESTS_URL,
  GET_ENVIRONMENT_VMS_FOR_DELETE_URL,
  GET_PENDING_ENVIRONMENT_URL,
  GET_PRODUCT_ACTION_METADATA_URL,
  GET_PRODUCT_INTEGRATIONS_URL,
  GET_SCALEOUT_VALIDATION_URL,
  GRACEFUL_PRODUCT_POWER_ON_URL,
  GRACEFUL_PRODUCT_SHUTDOWN_URL,
  MONITOR_VRA_WITH_VROPS_URL,
  PRODUCT_SNAPSHOT_URL,
  RETRUST_PRODUCT_WITH_LOAD_BALANCER_URL,
  RETRUST_VIDM_WITH_PRODUCT_URL,
  REVERT_TO_PRODUCT_SNAPSHOT_URL,
  RE_REGISTER_WITH_IDENTITY_MANAGER_URL,
  SUBMIT_PATCH_REQUEST_URL,
  SUBMIT_PRODUCT_ACTION_URL,
  TOGGLE_HEALTH_URL,
  TRIGGER_ENVIRONMENT_INVENTORY_SYNC_URL,
  UPDATE_NODE_PASSWORD_URL,
  UPDATE_VIDM_NODE_SSHUSER_PASSWORD_URL,
  VIDM_INTEGRATION_WITH_VSSC_URL,
  PRODUCT_SNAPSHOT_PRE_CHECK_URL,
  REVERT_TO_PRODUCT_NODES_SNAPSHOT_URL,
  PRODUCT_SNAPSHOT_REVERT_PRE_CHECK_URL,
  DELETE_ENVIRONMENT_LOGS_URL,
  VIDM_CLUSTER_AUTO_RECOVERY_URL
} from '../constants/ApiUri';
import { UpdateNodePassword } from '../types/ApiRequestPayload';
import { ActionResponse } from '../types/ApiResponseData';
import { ActionAccess, AssociatedVM } from '../types/Environments';
import { isCloudApp } from '../utils/ComponentHelpers';

export default class EnvironmentsAPI {
  public static getEnvironments = async (): Promise<EnvironmentState[]> => {
    const environments = await Http.get<EnvironmentState[]>(
      isCloudApp()
        ? GET_CLOUD_PROXY_ENVIRONMENT_REQUESTS()
        : GET_ALL_ENVIRONMENT_URL
    );
    return environments.data;
  };

  public static getPendingEnvironments = async (): Promise<
    EnvironmentState[]
  > => {
    const environments = await Http.get<EnvironmentState[]>(
      GET_PENDING_ENVIRONMENT_URL
    );
    return environments.data;
  };

  public static downloadEnvironmentLogs = async (
    environmentId: string
  ): Promise<string> => {
    const response = await Http.post(
      DOWNLOAD_ENVIRONMENT_LOGS_URL(environmentId)
    );
    return response.data.requestId;
  };

  public static deleteEnvironmentLogs = async (
    environmentId: string,
    logBundlePaths: string,
    productId?: string,
    tenantId?: string
  ): Promise<string> => {
    const response = await Http.delete(
      DELETE_ENVIRONMENT_LOGS_URL(
        environmentId,
        logBundlePaths,
        productId,
        tenantId
      )
    );
    return response.data.requestId;
  };

  public static downloadProductLogs = async (
    environmentId: string,
    productId: string,
    tenantId?: string
  ): Promise<string> => {
    const response = await Http.post(
      DOWNLOAD_PRODUCT_LOGS_URL(environmentId, productId, tenantId)
    );
    return response.data.requestId;
  };

  public static triggerEnvironmentInventorySync = async (
    environmentId: string
  ): Promise<void> => {
    await Http.post(TRIGGER_ENVIRONMENT_INVENTORY_SYNC_URL(environmentId));
  };

  public static toggleHeatlh = async (
    environmentId: string,
    status: 'enable' | 'disable'
  ): Promise<string> => {
    const response = await Http.post(TOGGLE_HEALTH_URL(environmentId, status));
    return response.data.requestId;
  };

  public static exportConfiguration = async (
    environmentId: string,
    type: string
  ): Promise<EnvironmentRequest> => {
    const response = await Http.get(
      EXPORT_CONFIGURATION_URL(environmentId, type)
    );
    return response.data;
  };

  public static exportProductConfiguration = async (
    environmentId: string,
    productId: string,
    type: string,
    tenantId?: string
  ): Promise<EnvironmentRequest> => {
    const response = await Http.get(
      EXPORT_PRODUCT_CONFIGURATION_URL(environmentId, productId, type, tenantId)
    );
    return response.data;
  };

  public static getProductActionMetadata = async (
    product: string,
    version: string,
    action: string
  ): Promise<GroupMetadata[]> => {
    const response = await Http.get<GroupMetadata[]>(
      GET_PRODUCT_ACTION_METADATA_URL(product, version, action)
    );
    return response.data;
  };

  public static getEnvironmentVMsForDelete = async (
    environmentId: string,
    deleteWindowsVMs: boolean
  ): Promise<AssociatedVM[]> => {
    const response = await Http.get(
      GET_ENVIRONMENT_VMS_FOR_DELETE_URL(environmentId, deleteWindowsVMs)
    );
    return response.data;
  };

  public static getProductIntegrations = async (
    environmentId: string,
    productId: string,
    tenantId?: string
  ): Promise<string[]> => {
    const response = await Http.get(
      GET_PRODUCT_INTEGRATIONS_URL(environmentId, productId, tenantId)
    );
    return response.data;
  };

  public static deleteEnvironment = async (
    environmentId: string,
    deleteOptions: DeleteOptions
  ): Promise<string> => {
    const response = await Http.delete(DELETE_ENVIRONMENT_URL(environmentId), {
      data: deleteOptions
    });
    return response.data.requestId;
  };

  public static deleteProduct = async (
    environmentId: string,
    productId: string,
    deleteOptions: DeleteOptions,
    tenantId?: string
  ): Promise<string> => {
    const response = await Http.delete(
      DELETE_PRODUCT_URL(environmentId, productId, tenantId),
      {
        data: deleteOptions
      }
    );
    return response.data.requestId;
  };

  public static getAvailablePatches = async (
    product: string,
    version: string,
    environmentId?: string
  ): Promise<PatchMetadata[]> => {
    const response = await Http.get(
      GET_AVAILABLE_PRODUCT_PATCHES_URL(product, version, environmentId)
    );
    return response.data;
  };

  public static getPatchHistory = async (
    product: string,
    version: string,
    environmentId: string
  ): Promise<PatchMetadata[]> => {
    const response = await Http.get(
      GET_AVAILABLE_PRODUCT_PATCHES_URL(product, version, environmentId)
    );
    return response.data;
  };

  public static installPatch = (
    environmentId: string,
    productId: string,
    patchId: string,
    tenantId?: string
  ): Promise<RequestId> => {
    return Http.post(
      SUBMIT_PATCH_REQUEST_URL(
        environmentId,
        productId,
        patchId,
        false,
        tenantId
      )
    ).then(response => response.data);
  };

  public static prevalidatePatch = (
    environmentId: string,
    productId: string,
    patchId: string
  ): Promise<RequestId> => {
    return Http.post(
      SUBMIT_PATCH_REQUEST_URL(environmentId, productId, patchId, true)
    ).then(response => response.data);
  };

  public static getCompatibleProducts = async (
    product: string,
    version: string
  ): Promise<CompatibleProduct[]> => {
    const response = await Http.get<CompatibleProduct[]>(
      GET_COMPATIBILITY_MATRIX(product, version)
    );
    return response.data;
  };

  public static submitProductAction = async (
    environmentId: string,
    productId: string,
    action: string,
    payload: any,
    tenantId: string
  ): Promise<string> => {
    const response = await Http.post(
      SUBMIT_PRODUCT_ACTION_URL(environmentId, productId, action, tenantId),
      payload
    );
    return response.data.requestId;
  };

  public static updateNodePassword = async (
    environmentId: string,
    productId: string,
    nodeType: string,
    payload: UpdateNodePassword,
    tenantId: string
  ): Promise<string> => {
    const response = await Http.post<ActionResponse>(
      productId === 'vidm' && payload.userNameToUpdate === 'sshuser'
        ? UPDATE_VIDM_NODE_SSHUSER_PASSWORD_URL(
            environmentId,
            productId,
            nodeType
          )
        : productId === 'vidm' &&
          (payload.userNameToUpdate === 'admin' ||
            payload.userNameToUpdate === 'systemadminpassword')
        ? UPDATE_VIDM_NODE_ADMIN_PASSWORD_URL(
            environmentId,
            productId,
            nodeType
          )
        : UPDATE_NODE_PASSWORD_URL(
            environmentId,
            productId,
            nodeType,
            tenantId
          ),
      payload
    );
    return response.data.requestId;
  };

  public static triggerMonitorVRAwithVROps = async (
    environmentId: string
  ): Promise<string> => {
    const response = await Http.post(MONITOR_VRA_WITH_VROPS_URL(environmentId));
    return response.data.requestId;
  };

  public static configureVROpsAsWlp = async (
    environmentId: string
  ): Promise<string> => {
    const response = await Http.post(CONFIGURE_VROPS_AS_WLP_URL(environmentId));
    return response.data.requestId;
  };

  public static getEnvironmentActiveRequests = async (
    environmentId: string
  ): Promise<string[]> => {
    const response = await Http.get(
      GET_ENVIRONMENT_ACTIVE_REQUESTS_URL(environmentId)
    );
    return response.data;
  };

  public static retrustVidmWithProduct = (
    environmentId: string,
    productId: string
  ): Promise<string> => {
    return Http.post<{ requestId: string }>(
      RETRUST_VIDM_WITH_PRODUCT_URL(environmentId, productId)
    ).then(res => res.data.requestId);
  };
  public static vidmIntegrationWithVssc = (
    environmentId: string,
    tenantId: string
  ): Promise<string> => {
    return Http.post<{ requestId: string }>(
      VIDM_INTEGRATION_WITH_VSSC_URL(environmentId, tenantId)
    ).then(res => res.data.requestId);
  };

  public static retrustProductWithLoadBalancer = (
    environmentId: string,
    productId: string,
    tenantId?: string
  ): Promise<string> => {
    return Http.post<{ requestId: string }>(
      RETRUST_PRODUCT_WITH_LOAD_BALANCER_URL(environmentId, productId, tenantId)
    ).then(res => res.data.requestId);
  };

  public static reRegisterVidmWithProduct = (
    environmentId: string,
    productId: string
  ): Promise<string> => {
    return Http.post<{ requestId: string }>(
      RE_REGISTER_WITH_IDENTITY_MANAGER_URL(environmentId, productId)
    ).then(res => res.data.requestId);
  };

  public static powerOnProduct = (
    environmentId: string,
    productId: string,
    rebootNodes?: boolean
  ): Promise<string> => {
    return Http.post<{ requestId: string }>(
      GRACEFUL_PRODUCT_POWER_ON_URL(environmentId, productId, rebootNodes)
    ).then(res => res.data.requestId);
  };

  public static gracefulProductShutdown = (
    environmentId: string,
    productId: string
  ): Promise<string> => {
    return Http.post<{ requestId: string }>(
      GRACEFUL_PRODUCT_SHUTDOWN_URL(environmentId, productId)
    ).then(res => res.data.requestId);
  };

  public static createProductSnapshot = async (
    environmentId: string,
    productId: string,
    tenantId: string,
    data: any
  ): Promise<string> => {
    const response = await Http.post(
      PRODUCT_SNAPSHOT_URL(environmentId, productId, tenantId),
      data
    );
    return response.data.requestId;
  };

  public static createProductSnapshotPreCheck = async (
    environmentId: string,
    productId: string,
    tenantId: string,
    data: {
      snapshotWithMemory: boolean;
      shutdownBeforeSnapshot: boolean;
    }
  ): Promise<string> => {
    const response = await Http.post(
      PRODUCT_SNAPSHOT_PRE_CHECK_URL(environmentId, productId, tenantId),
      data
    );
    return response.data.requestId;
  };

  public static getProductSnapshots = async (
    environmentId: string,
    productId: string,
    tenantId: string
  ): Promise<Snapshot[]> => {
    const response = await Http.get<Snapshot[]>(
      PRODUCT_SNAPSHOT_URL(environmentId, productId, tenantId)
    );
    return response.data;
  };

  public static revertToProductSnapshotPreCheck = async (
    environmentId: string,
    productId: string,
    tenantId: string,
    data: {
      powerOn: boolean;
      productSnapshotId: string;
    }
  ): Promise<string> => {
    const response = await Http.post(
      PRODUCT_SNAPSHOT_REVERT_PRE_CHECK_URL(environmentId, productId, tenantId),
      data
    );
    return response.data.requestId;
  };

  public static revertToProductSnapshot = async (
    environmentId: string,
    productId: string,
    tenantId: string,
    data: {
      powerOn: boolean;
      productSnapshotId: string;
    }
  ): Promise<string> => {
    const response = await Http.post(
      REVERT_TO_PRODUCT_SNAPSHOT_URL(environmentId, productId, tenantId),
      data
    );
    return response.data.requestId;
  };

  public static revertToProductNodesSnapshot = async (
    environmentId: string,
    productId: string,
    tenantId: string,
    data: any
  ): Promise<string> => {
    const response = await Http.post(
      REVERT_TO_PRODUCT_NODES_SNAPSHOT_URL(environmentId, productId, tenantId),
      data
    );
    return response.data.requestId;
  };

  public static deleteProductSnapshot = async (
    environmentId: string,
    productId: string,
    tenantId: string,
    data: {
      consolidate: boolean;
      productSnapshotId: string;
    }
  ): Promise<string> => {
    const response = await Http.delete(
      PRODUCT_SNAPSHOT_URL(environmentId, productId, tenantId),
      { data }
    );
    return response.data.requestId;
  };

  public static getEnvironmentActionsAccess = async (): Promise<
    ActionAccess
  > => {
    const { environmentActionAccess } = EnvironmentsAPI.accessCache;
    if (environmentActionAccess) {
      return environmentActionAccess;
    }
    const response = await Http.get<ActionAccess>(
      '/assets/env-actions-access.json'
    );
    EnvironmentsAPI.accessCache.environmentActionAccess = response.data;
    return response.data;
  };

  public static getProductActionsAccess = async (): Promise<ActionAccess> => {
    const { productActionAccess } = EnvironmentsAPI.accessCache;
    if (productActionAccess) {
      return productActionAccess;
    }
    const response = await Http.get<ActionAccess>(
      '/assets/product-actions-access.json'
    );
    EnvironmentsAPI.accessCache.productActionAccess = response.data;
    return response.data;
  };

  public static getNodeActionsAccess = async (): Promise<ActionAccess> => {
    const { nodeActionAccess } = EnvironmentsAPI.accessCache;
    if (nodeActionAccess) {
      return nodeActionAccess;
    }
    const response = await Http.get<ActionAccess>(
      '/assets/node-actions-access.json'
    );
    EnvironmentsAPI.accessCache.nodeActionAccess = response.data;
    return response.data;
  };

  public static validateProductScaleOutOva = async (
    environmentId: string,
    productId: string,
    productVersion: string
  ): Promise<string> => {
    const body = {
      environmentId,
      productId,
      productVersion
    };
    const response = await Http.post(GET_SCALEOUT_VALIDATION_URL, body);
    return response.data.requestId;
  };

  public static clusterAutoDiscovery = async (
    environmentId: string,
    productId: string,
    enable: boolean
  ): Promise<string> => {
    const response = await Http.post(
      VIDM_CLUSTER_AUTO_RECOVERY_URL(environmentId, productId, enable)
    );
    return response.data.requestId;
  };

  private static accessCache: {
    environmentActionAccess?: ActionAccess;
    productActionAccess?: ActionAccess;
    nodeActionAccess?: ActionAccess;
  } = {};
}
